import React, { useState, useRef, useEffect, useCallback } from 'react';
import { UnitInput } from '../UnitInput';
import { PrintQualityIndicator } from '../PrintQualityIndicator';
import { 
  mmToInches, 
  inchesToMm, 
  inchesToPoints, 
  pointsToInches, 
  inchesToPicas, 
  picasToInches 
} from '../../utils/conversions';

/**
 * Form-based converter panel component with simplified focus management
 */
export const ConverterPanel = ({
  dimensions,
  setDimensions,
  ppi: propPpi,
  dpi: propDpi,
  setPpi: setPpiGlobal,
  setDpi: setDpiGlobal,
  usesCustomPPI: propUsesCustomPPI,
  setUsesCustomPPI: setUsesCustomPPIGlobal,
  customPPI: propCustomPPI,
  setCustomPPI: setCustomPPIGlobal,
}) => {
  // Use local state to manage form values
  const [ppi, setPpi] = useState(propPpi);
  const [dpi, setDpi] = useState(propDpi);
  const [usesCustomPPI, setUsesCustomPPI] = useState(propUsesCustomPPI);
  const [customPPI, setCustomPPI] = useState(propCustomPPI);
  
  // Reference to the form element
  const formRef = useRef(null);
  
  // Flag to prevent circular updates
  const isUpdatingRef = useRef(false);
  
  // Sync with parent props when they change
  useEffect(() => {
    if (!isUpdatingRef.current) {
      setPpi(propPpi);
      setDpi(propDpi);
      setUsesCustomPPI(propUsesCustomPPI);
      setCustomPPI(propCustomPPI);
    }
  }, [propPpi, propDpi, propUsesCustomPPI, propCustomPPI]);
  
  // Handle unit changes
  const handleUnitChange = useCallback((dimension, unit, value) => {
    // Prevent circular updates
    if (isUpdatingRef.current) return;
    
    try {
      isUpdatingRef.current = true;
      
      // Create a copy of the current dimensions
      const newDimensions = { 
        width: { ...dimensions.width },
        height: { ...dimensions.height }
      };
      
      // Update the specific unit
      newDimensions[dimension][unit] = value;
      
      // If value is valid, calculate all other units
      if (value !== '' && !isNaN(parseFloat(value))) {
        let inches = 0;
        
        // Convert the input unit to inches (our base unit)
        switch(unit) {
          case 'mm':
            inches = mmToInches(parseFloat(value));
            break;
          case 'inches':
            inches = parseFloat(value);
            break;
          case 'points':
            inches = pointsToInches(parseFloat(value));
            break;
          case 'picas':
            inches = picasToInches(parseFloat(value));
            break;
          case 'pixels':
            // Convert pixels to inches based on PPI
            inches = parseFloat(value) / (usesCustomPPI ? customPPI : ppi);
            break;
          default:
            break;
        }
        
        // Calculate all other units
        if (unit !== 'mm') newDimensions[dimension].mm = inchesToMm(inches).toFixed(2);
        if (unit !== 'inches') newDimensions[dimension].inches = inches.toFixed(3);
        if (unit !== 'points') newDimensions[dimension].points = inchesToPoints(inches).toFixed(1);
        if (unit !== 'picas') newDimensions[dimension].picas = inchesToPicas(inches).toFixed(2);
        if (unit !== 'pixels') newDimensions[dimension].pixels = Math.round(inches * (usesCustomPPI ? customPPI : ppi));
      }
      
      // Update dimensions in parent
      setDimensions(newDimensions);
    } finally {
      // Reset the update flag after a short delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 50);
    }
  }, [dimensions, setDimensions, ppi, customPPI, usesCustomPPI]);
  
  // Handle PPI change
  const handlePPIChange = useCallback((e) => {
    const value = e.target.value;
    
    try {
      isUpdatingRef.current = true;
      
      if (value === 'custom') {
        // Switch to custom PPI mode
        setUsesCustomPPI(true);
        setUsesCustomPPIGlobal(true);
      } else {
        // Set to preset PPI value
        const newPpi = parseInt(value);
        setUsesCustomPPI(false);
        setPpi(newPpi);
        
        // Update global state
        setUsesCustomPPIGlobal(false);
        setPpiGlobal(newPpi);
        
        // Update pixel dimensions
        if (dimensions.width.inches && dimensions.height.inches) {
          const widthInches = parseFloat(dimensions.width.inches);
          const heightInches = parseFloat(dimensions.height.inches);
          
          if (!isNaN(widthInches) && !isNaN(heightInches)) {
            const newDimensions = { ...dimensions };
            newDimensions.width.pixels = Math.round(widthInches * newPpi);
            newDimensions.height.pixels = Math.round(heightInches * newPpi);
            setDimensions(newDimensions);
          }
        }
      }
    } finally {
      // Reset the update flag after a short delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 50);
    }
  }, [dimensions, setDimensions, setPpiGlobal, setUsesCustomPPIGlobal]);
  
  // Handle custom PPI change
  const handleCustomPPIChange = useCallback((e) => {
    const newValue = parseInt(e.target.value);
    
    if (!isNaN(newValue) && newValue > 0) {
      try {
        isUpdatingRef.current = true;
        
        // Update local and global state
        setCustomPPI(newValue);
        setCustomPPIGlobal(newValue);
        
        // Update pixel dimensions if using custom PPI
        if (usesCustomPPI && dimensions.width.inches && dimensions.height.inches) {
          const widthInches = parseFloat(dimensions.width.inches);
          const heightInches = parseFloat(dimensions.height.inches);
          
          if (!isNaN(widthInches) && !isNaN(heightInches)) {
            const newDimensions = { ...dimensions };
            newDimensions.width.pixels = Math.round(widthInches * newValue);
            newDimensions.height.pixels = Math.round(heightInches * newValue);
            setDimensions(newDimensions);
          }
        }
      } finally {
        // Reset the update flag after a short delay
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 50);
      }
    }
  }, [dimensions, setDimensions, usesCustomPPI, setCustomPPIGlobal]);
  
  // Handle DPI change
  const handleDPIChange = useCallback((e) => {
    const value = parseInt(e.target.value);
    
    try {
      isUpdatingRef.current = true;
      
      // Update local and global state
      setDpi(value);
      setDpiGlobal(value);
    } finally {
      // Reset the update flag after a short delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 50);
    }
  }, [setDpiGlobal]);
  
  // Calculate if current dimensions meet print quality
  const getPrintQualityStatus = useCallback(() => {
    if (!dimensions.width.pixels || !dimensions.height.pixels) {
      return { status: 'neutral', message: 'Enter dimensions to check print quality' };
    }
    
    const widthInches = parseFloat(dimensions.width.inches);
    const heightInches = parseFloat(dimensions.height.inches);
    
    if (isNaN(widthInches) || isNaN(heightInches)) {
      return { status: 'neutral', message: 'Invalid dimensions' };
    }
    
    const widthPixels = parseInt(dimensions.width.pixels);
    const heightPixels = parseInt(dimensions.height.pixels);
    
    // Calculate actual PPI
    const actualPPI = widthPixels / widthInches;
    
    // Compare to required DPI
    const ratio = actualPPI / dpi;
    
    if (ratio >= 1) {
      return { 
        status: 'good', 
        message: `Good - ${Math.round(actualPPI)} PPI exceeds required ${dpi} DPI` 
      };
    } else if (ratio >= 0.7) {
      return { 
        status: 'fair', 
        message: `Fair - ${Math.round(actualPPI)} PPI may show slight pixelation at ${dpi} DPI` 
      };
    } else {
      return { 
        status: 'poor', 
        message: `Poor - ${Math.round(actualPPI)} PPI will appear pixelated at ${dpi} DPI` 
      };
    }
  }, [dimensions, dpi]);
  
  // Calculate required pixels based on physical size and DPI
  const getRequiredPixels = useCallback(() => {
    const widthInches = parseFloat(dimensions.width.inches);
    const heightInches = parseFloat(dimensions.height.inches);
    
    if (isNaN(widthInches) || isNaN(heightInches)) {
      return '—';
    }
    
    const requiredWidth = Math.ceil(widthInches * dpi);
    const requiredHeight = Math.ceil(heightInches * dpi);
    
    return `${requiredWidth} × ${requiredHeight}`;
  }, [dimensions, dpi]);
  
  // Calculate total megapixels
  const totalPixels = dimensions.width.pixels && dimensions.height.pixels
    ? (parseFloat(dimensions.width.pixels) * parseFloat(dimensions.height.pixels) / 1000000).toFixed(2)
    : '0.00';

  return (
    <form 
      className="w-full h-full overflow-y-auto flex flex-col bg-white border-r border-gray-200"
      onSubmit={(e) => e.preventDefault()} // Prevent form submission
      ref={formRef}
    >
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-800">Measurement Converter</h2>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Physical Dimensions</h3>
          <div className="grid grid-cols-[auto_1fr_1fr] gap-x-4 gap-y-3 items-center">
            <div></div>
            <div className="text-center text-xs text-gray-600 font-medium">Width</div>
            <div className="text-center text-xs text-gray-600 font-medium">Height</div>
            
            <span className="text-xs text-gray-700">mm</span>
            <UnitInput
              value={dimensions.width.mm}
              onChange={(value) => handleUnitChange('width', 'mm', value)}
              placeholder="0.00"
              name="width-mm"
            />
            <UnitInput
              value={dimensions.height.mm}
              onChange={(value) => handleUnitChange('height', 'mm', value)}
              placeholder="0.00"
              name="height-mm"
            />
            
            <span className="text-xs text-gray-700">in</span>
            <UnitInput
              value={dimensions.width.inches}
              onChange={(value) => handleUnitChange('width', 'inches', value)}
              placeholder="0.00"
              name="width-inches"
            />
            <UnitInput
              value={dimensions.height.inches}
              onChange={(value) => handleUnitChange('height', 'inches', value)}
              placeholder="0.00"
              name="height-inches"
            />
            
            <span className="text-xs text-gray-700">pt</span>
            <UnitInput
              value={dimensions.width.points}
              onChange={(value) => handleUnitChange('width', 'points', value)}
              placeholder="0.00"
              name="width-points"
            />
            <UnitInput
              value={dimensions.height.points}
              onChange={(value) => handleUnitChange('height', 'points', value)}
              placeholder="0.00"
              name="height-points"
            />
            
            <span className="text-xs text-gray-700">pc</span>
            <UnitInput
              value={dimensions.width.picas}
              onChange={(value) => handleUnitChange('width', 'picas', value)}
              placeholder="0.00"
              name="width-picas"
            />
            <UnitInput
              value={dimensions.height.picas}
              onChange={(value) => handleUnitChange('height', 'picas', value)}
              placeholder="0.00"
              name="height-picas"
            />
          </div>
          
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div className="col-span-3 bg-blue-50 p-2 rounded text-center">
              <span className="text-xs text-blue-800">Physical Size: {dimensions.width.mm || '0.00'} × {dimensions.height.mm || '0.00'} mm</span>
            </div>
            <div className="bg-gray-50 p-2 rounded text-center">
              <span className="text-xs text-gray-800">Area: {
                dimensions.width.mm && dimensions.height.mm ? 
                (parseFloat(dimensions.width.mm) * parseFloat(dimensions.height.mm) / 100).toFixed(2) : 
                '0.00'} cm²</span>
            </div>
            <div className="bg-gray-50 p-2 rounded text-center">
              <span className="text-xs text-gray-800">Ratio: {
                dimensions.width.mm && dimensions.height.mm ? 
                (parseFloat(dimensions.width.mm) / parseFloat(dimensions.height.mm)).toFixed(2) : 
                '0.00'}</span>
            </div>
            <div className="bg-gray-50 p-2 rounded text-center">
              <span className="text-xs text-gray-800">Diagonal: {
                dimensions.width.mm && dimensions.height.mm ? 
                Math.sqrt(Math.pow(parseFloat(dimensions.width.mm), 2) + Math.pow(parseFloat(dimensions.height.mm), 2)).toFixed(2) : 
                '0.00'} mm</span>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Digital Dimensions</h3>
          <div className="mb-3">
            <label className="text-xs text-gray-600 mb-1 block">Resolution</label>
            <select 
              className="w-full text-xs p-2 border border-gray-300 rounded"
              value={usesCustomPPI ? 'custom' : ppi.toString()}
              onChange={handlePPIChange}
              tabIndex={0}
            >
              <option value="72">72 PPI - Screen/Web</option>
              <option value="150">150 PPI - Draft Print</option>
              <option value="300">300 PPI - Standard Print</option>
              <option value="600">600 PPI - High Quality Print</option>
              <option value="1200">1200 PPI - Professional Print</option>
              <option value="custom">Custom PPI</option>
            </select>
          </div>
          
          {usesCustomPPI && (
            <div className="mb-3">
              <label className="text-xs text-gray-600 mb-1 block">Custom PPI</label>
              <input
                type="number"
                className="w-full text-xs p-2 border border-gray-300 rounded"
                value={customPPI}
                onChange={handleCustomPPIChange}
                min="1"
                step="1"
                tabIndex={0}
              />
            </div>
          )}
          
          <div className="mb-3">
            <label className="text-xs text-gray-600 mb-1 block">Print Quality (DPI)</label>
            <select 
              className="w-full text-xs p-2 border border-gray-300 rounded"
              value={dpi.toString()}
              onChange={handleDPIChange}
              tabIndex={0}
            >
              <option value="150">150 DPI - Draft</option>
              <option value="300">300 DPI - Standard</option>
              <option value="600">600 DPI - High Quality</option>
              <option value="1200">1200 DPI - Professional</option>
            </select>
          </div>
          
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 items-center">
            <span className="text-xs text-gray-700">Width</span>
            <UnitInput
              value={dimensions.width.pixels}
              onChange={(value) => handleUnitChange('width', 'pixels', value)}
              placeholder="px"
              type="number"
              min="1"
              name="width-pixels"
            />
            
            <span className="text-xs text-gray-700">Height</span>
            <UnitInput
              value={dimensions.height.pixels}
              onChange={(value) => handleUnitChange('height', 'pixels', value)}
              placeholder="px"
              type="number"
              min="1"
              name="height-pixels"
            />
          </div>
          
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="col-span-2 bg-blue-50 p-2 rounded text-center">
              <span className="text-xs text-blue-800">Total Resolution: <strong>{totalPixels} MP</strong></span>
            </div>
            
            <div className="bg-gray-50 p-2 rounded text-center">
              <span className="text-xs text-gray-800">File Size (8-bit): {
                dimensions.width.pixels && dimensions.height.pixels ? 
                (parseInt(dimensions.width.pixels) * parseInt(dimensions.height.pixels) * 3 / (1024 * 1024)).toFixed(2) : 
                '0.00'} MB</span>
            </div>
            
            <div className="bg-gray-50 p-2 rounded text-center">
              <span className="text-xs text-gray-800">File Size (16-bit): {
                dimensions.width.pixels && dimensions.height.pixels ? 
                (parseInt(dimensions.width.pixels) * parseInt(dimensions.height.pixels) * 6 / (1024 * 1024)).toFixed(2) : 
                '0.00'} MB</span>
            </div>
          </div>
        </div>
        
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Print Requirements</h3>
          <div className="mb-3">
            <PrintQualityIndicator status={getPrintQualityStatus()} />
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            <div className="bg-gray-50 p-2 rounded">
              <span className="text-xs text-gray-800">Required pixels at {dpi} DPI: <span className="font-medium">{getRequiredPixels()}</span></span>
            </div>
            
            <div className="bg-gray-50 p-2 rounded">
              <span className="text-xs text-gray-800">Actual Resolution: {
                dimensions.width.inches && dimensions.width.pixels ? 
                Math.round(parseInt(dimensions.width.pixels) / parseFloat(dimensions.width.inches)) : 
                '0'} PPI</span>
            </div>
          </div>
        </div>
        
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Common Uses</h3>
          <div className="bg-gray-50 p-3 rounded text-xs text-gray-600 space-y-2">
            <p>• <strong>Social Media:</strong> 72-150 PPI</p>
            <p>• <strong>Office Printing:</strong> 300 DPI</p>
            <p>• <strong>Professional Print:</strong> 300-600 DPI</p>
            <p>• <strong>Fine Art Printing:</strong> 600+ DPI</p>
            <p>• <strong>Large Format:</strong> 150-300 DPI (viewed from distance)</p>
          </div>
        </div>
      </div>
    </form>
  );
};